import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { User, Loader2 } from 'lucide-react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
    Search, ArrowUp, ArrowDown, ArrowUpDown, Pencil, X, Trash2, Plus, Check
} from 'lucide-react';
import { GET, POST, PUT, DELETE, FORM_POST } from '../services/api';
import { format } from 'date-fns';
import { useMasterChapter, MasterChapterLockNote } from '../context/MasterChapter';

// --- CONFIGURATION ---
const TABLE_CONFIG = {
  revenue: {
    title: 'Business Revenue',
    endpoint: '/business-exchange-json',
    columns: [
      { key: 'from_member.name', label: 'From Member', sortable: true, isAvatar: true, picPath: 'from_member' },
      { key: 'to_member.name', label: 'To Member', sortable: true },
      { key: 'amount', label: 'Amount', isCurrency: true, sortable: true },
      { key: 'date', label: 'Date', isDate: true, sortable: true },
      { key: 'remarks', label: 'Remarks' },
    ]
  },
  members: {
    title: 'Member Directory',
    endpoint: '/export-members-json',
    columns: [
      { key: 'member_name', label: 'Name', sortable: true, isAvatar: true, picPath: '' },
      { key: 'member_email', label: 'Email', sortable: true },
      { key: 'city', label: 'City', sortable: true },
      { key: 'chapter', label: 'Chapter', sortable: true },
      { key: 'business.name', label: 'Business' },
      { key: 'joining_date', label: 'Joined On', isDate: true, sortable: true },
    ]
  },
  references: {
    title: 'Reference Passes',
    endpoint: '/reference-passes-json',
    columns: [
      { key: 'from_member_name', label: 'Given By', sortable: true, isAvatar: true, picPath: 'from_member' },
      { key: 'to_member_name', label: 'Received By', sortable: true },
      { key: 'date', label: 'Date', isDate: true, sortable: true },
      { key: 'remarks', label: 'Details' },
    ]
  },
  meetings: {
    title: 'Personal Meetings',
    endpoint: '/personal-meetings-json',
    columns: [
      { key: 'host_name', label: 'Host', sortable: true, isAvatar: true, picPath: 'host' },
      { key: 'visitor_name', label: 'Visitor', sortable: true },
      { key: 'created_at', label: 'Meeting Date', isDate: true, sortable: true },
      { key: 'remarks', label: 'Notes' },
    ]
  }
};

// Helper to safely get nested properties (e.g. "business.name")
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};



const getMemberPic = (row, memberPath) => {
  console.log('ROW:', row);
  console.log('PIC PATH:', memberPath);
  
  let profilePic = null;

  // 1. Try direct root
  if (row.profile_picture) {
    profilePic = row.profile_picture;
  }

  // 2. Try configured path (IMPORTANT FIX)
  if (!profilePic && memberPath) {
    const member = getNestedValue(row, memberPath);
    profilePic = member?.profile_picture;
  }

  // 3. Try FLAT image fields from API (CRITICAL FIX)
  if (!profilePic) {
    profilePic =
      row.host_profile_picture ||
      row.visitor_profile_picture ||
      row.from_member_profile_picture ||
      row.to_member_profile_picture ||
      null;
  }

  // 4. Fallback to nested objects
  if (!profilePic) {
    profilePic =
      row?.from_member?.profile_picture ||
      row?.to_member?.profile_picture ||
      row?.host?.profile_picture ||
      row?.visitor?.profile_picture ||
      null;
  }

  console.log('FINAL PIC:', profilePic);

  // The API already returns display URLs for stored files.
  console.log('RETURNED PIC:', profilePic);
  return profilePic;
};

const Avatar = ({ src, name, size = 'sm' }) => {
  const [error, setError] = useState(false);
  
const absoluteSrc = src || null;
  
  return (
    <div className={`w-8 h-8 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center ${size === 'lg' ? 'w-10 h-10' : ''}`}>
      {src && !error ? (
        <img
          src={absoluteSrc}
          alt={name || 'Avatar'}
          className="w-full h-full object-cover"
          onError={() => setError(true)}
        />
      ) : (
        <span className="text-xs font-bold text-gray-600">
          {name ? (name.includes(' ') ? name.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase() : name.substring(0, 2).toUpperCase()) : '??'}
        </span>
      )}
    </div>
  );
};

const getTodayInputDate = () => format(new Date(), 'yyyy-MM-dd');

// --- Reusable member search field (inline dropdown, no absolute positioning so no clipping) ---
const MemberSearchField = ({ label, value, memberId, onChange, onSelect, onClear, results, isSearching, required = false }) => (
    <div>
        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">{label}</label>
        {memberId ? (
            <div className="flex items-center gap-2.5 px-3 py-2.5 bg-blue-50 border border-blue-200 rounded-xl">
                <div className="w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {value ? value.charAt(0).toUpperCase() : '?'}
                </div>
                <span className="text-sm font-semibold text-blue-900 flex-1 truncate">{value}</span>
                <button type="button" onClick={onClear} className="text-blue-400 hover:text-red-500 transition-colors" title="Change member">
                    <X className="w-4 h-4" />
                </button>
            </div>
        ) : (
            <>
                <div className="relative">
                    <input
                        type="text"
                        value={value}
                        onChange={onChange}
                        placeholder="Type at least 2 letters to search..."
                        className="w-full px-4 py-2.5 pr-10 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        required={required}
                        autoComplete="off"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                        {isSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                    </div>
                </div>
                {results.length > 0 && (
                    <div className="mt-1 border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                        <div className="max-h-40 overflow-y-auto">
                            {results.map((m) => (
                                <button key={m._id} type="button" onClick={() => onSelect(m)}
                                    className="w-full text-left px-4 py-2.5 hover:bg-blue-50 border-b border-slate-50 last:border-b-0 transition-colors">
                                    <p className="text-sm font-semibold text-slate-800">{m.full_name}</p>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </>
        )}
    </div>
);

const Toast = ({ toast, onClose }) => {
    if (!toast?.message) return null;

    return (
        <div className={`fixed bottom-6 right-6 px-5 py-3 rounded-xl shadow-2xl text-white font-semibold flex items-center gap-3 z-[1000] ${toast.type === 'success' ? 'bg-emerald-600' : 'bg-red-500'}`}>
            {toast.type === 'success' ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
            <span>{toast.message}</span>
            <button type="button" onClick={onClose} className="ml-1 p-1 rounded-full hover:bg-white/15 transition-colors">
                <X className="w-4 h-4" />
            </button>
        </div>
    );
};

const ViewData = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
    const normalizedType = (type || '').toLowerCase();
    const typeAliasMap = {
        reference: 'references',
        references: 'references',
        revenue: 'revenue',
        businessrevenue: 'revenue',
        member: 'members',
        members: 'members',
        meeting: 'meetings',
        meetings: 'meetings'
    };
    const resolvedType = typeAliasMap[normalizedType] || normalizedType;
  
  // --- FIX: Stabilize Filters to prevent Infinite Loop ---
  // Hum values ko alag variables me nikal rahe hain. 
  // Agar location.state null hai (New Tab), to ye variables undefined rahenge (Stable).
  // Ye '|| {}' hatane se naya object banna band ho jayega.
  const stateFilters = location.state?.filters;
  
  // Super-admin master chapter overrides whatever the Dashboard passed (or a bare new tab)
  const master = useMasterChapter();
  const filterCityId = master.chapterId ? master.cityId : stateFilters?.cityId;
  const filterChapterId = master.chapterId || stateFilters?.chapterId;
  const filterStartDate = stateFilters?.startDate;
  const filterEndDate = stateFilters?.endDate;
  const filterLabel = master.chapterId ? `${master.cityName} | ${master.chapterName}` : stateFilters?.label;

  // States

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Client-Side Search & Sort State
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
    const [showEditModal, setShowEditModal] = useState(false);
    const [editSubmitting, setEditSubmitting] = useState(false);
    const [editForm, setEditForm] = useState({
        id: '',
        from_member_id: '',
        from_member_name: '',
        to_member_id: '',
        to_member_name: '',
        amount: '',
        date: '',
        remarks: '',
        meeting_date: '',
        location: 'other'
    });
    // Add modal state
    const [showAddModal, setShowAddModal] = useState(false);
    const [addSubmitting, setAddSubmitting] = useState(false);
    const [addImageFile, setAddImageFile] = useState(null);
    const [addForm, setAddForm] = useState({
        from_member_id: '', from_member_name: '',
        to_member_id: '', to_member_name: '',
        amount: '', date: getTodayInputDate(), remarks: '',
        refName: '', phone: '',
        host_id: '', host_name: '',
        visitor_id: '', visitor_name: '',
        location: 'other', meeting_date: getTodayInputDate()
    });
    const [addFromResults, setAddFromResults] = useState([]);
    const [addToResults, setAddToResults] = useState([]);
    const [isAddSearchingFrom, setIsAddSearchingFrom] = useState(false);
    const [isAddSearchingTo, setIsAddSearchingTo] = useState(false);
    const [fromMemberResults, setFromMemberResults] = useState([]);
    const [toMemberResults, setToMemberResults] = useState([]);
    const [isSearchingFrom, setIsSearchingFrom] = useState(false);
    const [isSearchingTo, setIsSearchingTo] = useState(false);
    const searchTimerRef = useRef(null);
    const toastTimerRef = useRef(null);
    const [toast, setToast] = useState(null);

    // Get configuration or fallback to 'members' to prevent crash
        const currentConfig = TABLE_CONFIG[resolvedType] || TABLE_CONFIG.members;
        const userStr = localStorage.getItem('user');
        const user = userStr ? JSON.parse(userStr) : {};
        const isSuperAdmin = user.role === 'super-admin';

    const showToast = useCallback((message, type = 'success') => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ message, type });
        toastTimerRef.current = setTimeout(() => setToast(null), 3000);
    }, []);

    useEffect(() => {
        return () => {
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        };
    }, []);

  // --- Fetch ALL Data API ---
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
        const params = new URLSearchParams();
        // Load virtually all records for client-side manipulation
        params.append('limit', 10000); 
        params.append('page', 1);
        
        // Dashboard se aaye Filters Apply karein (Use stable variables)
        if (filterCityId) params.append('cityId', filterCityId);
        if (filterChapterId) params.append('chapterId', filterChapterId);
        if (filterStartDate) params.append('startDate', filterStartDate);
        if (filterEndDate) params.append('endDate', filterEndDate);
        
        const response = await GET(`${currentConfig.endpoint}?${params.toString()}`);
        if (response && response.data) {
            // Ensure data is always an array
            setData(Array.isArray(response.data) ? response.data : []);
        } else {
            setData([]);
        }
    } catch (error) {
        console.error("Data fetch error:", error);
        setData([]);
                showToast(error?.message || 'Failed to load data.', 'error');
    } finally {
        setLoading(false);
    }
    }, [currentConfig, filterCityId, filterChapterId, filterStartDate, filterEndDate, showToast]); 
  // Upar dependency array me ab sirf primitive values hain (Strings), Objects nahi.

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- Client-Side Filter & Sort Logic ---
  const filteredAndSortedData = useMemo(() => {
    let processedData = [...data];

    // 1. Search Logic
    if (search.trim()) {
        const term = search.toLowerCase();
        processedData = processedData.filter(item => {
            // Generic search across all configured columns
            return currentConfig.columns.some(col => {
                const val = getNestedValue(item, col.key);
                return val && String(val).toLowerCase().includes(term);
            });
        });
    }

    // 2. Sort Logic
    if (sortConfig.key) {
        processedData.sort((a, b) => {
            let valA = getNestedValue(a, sortConfig.key);
            let valB = getNestedValue(b, sortConfig.key);

            // Handle undefined/null values
            if (valA === undefined || valA === null) valA = '';
            if (valB === undefined || valB === null) valB = '';

            // Check if values are numbers (or numeric strings)
            const isNumeric = !isNaN(parseFloat(valA)) && isFinite(valA) && !isNaN(parseFloat(valB)) && isFinite(valB);

            if (isNumeric) {
                valA = parseFloat(valA);
                valB = parseFloat(valB);
            } else {
                // Convert to string for comparison if not numeric
                valA = String(valA).toLowerCase();
                valB = String(valB).toLowerCase();
            }

            if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
            if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    }

    return processedData;
  }, [data, search, sortConfig, currentConfig]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

    const canEditRows = (resolvedType === 'revenue' || resolvedType === 'references' || resolvedType === 'meetings') && isSuperAdmin;

    const openEditModal = (row) => {
        if (resolvedType === 'meetings') {
            setEditForm({
                id: row._id || '',
                from_member_id: row.host_id || '',
                from_member_name: row.host_name || '',
                to_member_id: row.visitor_id || '',
                to_member_name: row.visitor_name || '',
                amount: '',
                date: '',
                remarks: row.remarks || '',
                meeting_date: row.meeting_date ? format(new Date(row.meeting_date), 'yyyy-MM-dd') : (row.created_at ? format(new Date(row.created_at), 'yyyy-MM-dd') : ''),
                location: row.location || 'other'
            });
        } else {
            setEditForm({
                id: row._id || row.id || '',
                from_member_id: row.from_member_id || row.given_by || '',
                from_member_name: row.from_member?.name || row.from_member_name || '',
                to_member_id: row.to_member_id || row.received_by || '',
                to_member_name: row.to_member?.name || row.to_member_name || '',
                amount: row.amount ?? '',
                date: row.date ? format(new Date(row.date), 'yyyy-MM-dd') : '',
                remarks: row.remarks || row.details || '',
                meeting_date: '',
                location: 'other'
            });
        }
        setFromMemberResults([]);
        setToMemberResults([]);
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        setEditSubmitting(false);
        setFromMemberResults([]);
        setToMemberResults([]);
    };

    // Debounced member search — fires directly from onChange, no useEffect
    const searchMembers = useCallback((query, setResults, setIsSearching) => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        if (!query || query.trim().length < 2) {
            setResults([]);
            setIsSearching(false);
            return;
        }
        searchTimerRef.current = setTimeout(async () => {
            setIsSearching(true);
            try {
                const res = await GET(`/app/search-businesses?search=${encodeURIComponent(query.trim())}`);
                setResults(res?.success && Array.isArray(res.data) ? res.data : []);
            } catch { setResults([]); }
            finally { setIsSearching(false); }
        }, 350);
    }, []);

    const handleEditSubmit = async (event) => {
        event.preventDefault();
        if (!editForm.id) return;
        if (!editForm.from_member_id || !editForm.to_member_id) {
            showToast('Please select valid members from the search list.', 'error');
            return;
        }

        setEditSubmitting(true);
        try {
            const payload = {
                from_member_id: editForm.from_member_id,
                to_member_id: editForm.to_member_id,
                date: editForm.date,
                remarks: editForm.remarks
            };

            if (resolvedType === 'revenue') {
                await PUT(`/business-exchange/${editForm.id}`, {
                    ...payload,
                    amount: Number(editForm.amount)
                });
            } else if (resolvedType === 'references') {
                await PUT(`/reference-pass/${editForm.id}`, payload);
            } else if (resolvedType === 'meetings') {
                await PUT(`/personal-meeting/${editForm.id}`, {
                    host_id: editForm.from_member_id,
                    visitor_id: editForm.to_member_id,
                    location: editForm.location,
                    meeting_date: editForm.meeting_date,
                    remarks: editForm.remarks
                });
            }

            closeEditModal();
            await fetchData();
            showToast(`${currentConfig.title.slice(0, -1) || 'Record'} updated successfully.`, 'success');
        } catch (error) {
            console.error('Update failed:', error);
            showToast(error?.message || 'Failed to update record.', 'error');
        } finally {
            setEditSubmitting(false);
        }
    };

    // --- Delete ---
    const handleDelete = async (row) => {
        if (!window.confirm('Are you sure you want to delete this record?')) return;
        const rowId = row._id || row.id;
        const previousData = data;

        setData(prev => prev.filter(item => (item._id || item.id) !== rowId));

        try {
            if (resolvedType === 'revenue') {
                await DELETE(`/networking/business-exchange/${rowId}`);
            } else if (resolvedType === 'references') {
                await DELETE(`/networking/reference-pass/${rowId}`);
            } else if (resolvedType === 'meetings') {
                await DELETE(`/networking/personal-meeting/${rowId}`);
            }
            showToast('Record deleted successfully.', 'success');
            fetchData();
        } catch (error) {
            console.error('Delete failed:', error);
            setData(previousData);
            showToast(error?.message || 'Failed to delete record.', 'error');
        }
    };

    // --- Add Modal ---
    const openAddModal = () => {
        setAddForm({
            from_member_id: '', from_member_name: '',
            to_member_id: '', to_member_name: '',
            amount: '', date: getTodayInputDate(), remarks: '',
            refName: '', phone: '',
            host_id: '', host_name: '',
            visitor_id: '', visitor_name: '',
            location: 'other', meeting_date: getTodayInputDate()
        });
        setAddImageFile(null);
        setAddFromResults([]);
        setAddToResults([]);
        setShowAddModal(true);
    };

    const closeAddModal = () => {
        setShowAddModal(false);
        setAddSubmitting(false);
        setAddFromResults([]);
        setAddToResults([]);
        setAddImageFile(null);
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();
        setAddSubmitting(true);
        try {
            if (resolvedType === 'revenue') {
                if (!addForm.from_member_id || !addForm.to_member_id) {
                    showToast('Please select valid From and To members.', 'error');
                    return;
                }
                await POST('/networking/business-exchange', {
                    from_member_id: addForm.from_member_id,
                    to_member_id: addForm.to_member_id,
                    amount: Number(addForm.amount),
                    date: addForm.date || undefined,
                    remarks: addForm.remarks
                });
            } else if (resolvedType === 'references') {
                if (!addForm.from_member_id || !addForm.to_member_id) {
                    showToast('Please select valid From and To members.', 'error');
                    return;
                }
                await POST('/networking/reference-pass', {
                    from_member_id: addForm.from_member_id,
                    to_member_id: addForm.to_member_id,
                    refName: addForm.refName || undefined,
                    phone: addForm.phone || undefined,
                    date: addForm.date || undefined,
                    remarks: addForm.remarks
                });
            } else if (resolvedType === 'meetings') {
                if (!addForm.host_id || !addForm.visitor_id) {
                    showToast('Please select valid Host and Visitor members.', 'error');
                    return;
                }
                const fd = new FormData();
                fd.append('host_id', addForm.host_id);
                fd.append('visitor_id', addForm.visitor_id);
                fd.append('location', addForm.location);
                fd.append('remarks', addForm.remarks);
                if (addForm.meeting_date) fd.append('meeting_date', addForm.meeting_date);
                if (addImageFile) fd.append('one_to_one_meeting_image', addImageFile);
                await FORM_POST('/networking/personal-meeting', fd);
            }
            closeAddModal();
            await fetchData();
            showToast('Record created successfully.', 'success');
        } catch (error) {
            console.error('Add failed:', error);
            showToast(error?.message || 'Failed to create record.', 'error');
        } finally {
            setAddSubmitting(false);
        }
    };

  return (
    <div>
      <div className="space-y-5">
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
                <h1 className="text-2xl font-bold text-slate-800">{currentConfig.title}</h1>
                <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                    <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded border border-blue-100 font-medium">
                        Filtered by: {filterLabel || 'Default View'}
                    </span>
                    <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded border border-slate-200">
                        Total Records: {filteredAndSortedData.length}
                    </span>
                </div>
                <div className="mt-1"><MasterChapterLockNote /></div>
            </div>
            <div className="flex gap-3">
                {canEditRows && (
                    <button onClick={openAddModal} className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium transition shadow-sm">
                        <Plus className="w-4 h-4" />
                        Add New
                    </button>
                )}
                <button onClick={() => navigate(-1)} className="px-4 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 font-medium transition shadow-sm">
                    Back to Dashboard
                </button>
            </div>
        </div>

        {/* Search Bar Only (Refresh Removed) */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-200 flex justify-between items-center gap-4">
            <div className="relative flex-1 max-w-md">
                <label className="absolute -top-2 left-3 bg-white px-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Search</label>
                <input 
                    type="text" 
                    value={search} 
                    onChange={(e) => setSearch(e.target.value)} 
                    placeholder={`Search by Name, Business, etc...`} 
                    className="w-full p-2.5 pl-10 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-700" 
                />
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3.5" />
            </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-100">
                    <thead className="bg-slate-50/50">
                        <tr>
                            <th className="px-5 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">#</th>
                            {currentConfig.columns.map((col, idx) => (
                                <th 
                                    key={idx} 
                                    className={`px-5 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider ${col.sortable ? 'cursor-pointer hover:text-blue-600 group' : ''}`} 
                                    onClick={() => col.sortable && handleSort(col.key)}
                                >
                                    <div className="flex items-center gap-1">
                                        {col.label}
                                        {col.sortable && (
                                            <span className="text-slate-300 group-hover:text-blue-500">
                                                {sortConfig.key === col.key ? (
                                                    sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3"/> : <ArrowDown className="w-3 h-3"/>
                                                ) : (
                                                    <ArrowUpDown className="w-3 h-3"/>
                                                )}
                                            </span>
                                        )}
                                    </div>
                                </th>
                            ))}

                                <Toast toast={toast} onClose={() => setToast(null)} />
                                                        {canEditRows && (
                                                            <th className="px-5 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider sticky right-0 bg-slate-50/95 z-10">Actions</th>
                                                        )}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-100">
                        {loading ? (
                            Array.from({ length: 5 }).map((_, rowIdx) => (
                                <tr key={`skeleton-${rowIdx}`} className="hover:bg-slate-50/80 transition-colors">
                                    <td className="px-5 py-3 whitespace-nowrap text-sm font-bold text-slate-500">
                                        <div className="h-4 bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 rounded animate-pulse" style={{width: '20px'}}></div>
                                    </td>
                                    {currentConfig.columns.map((col, colIdx) => (
                                        <td key={`${rowIdx}-${colIdx}`} className="px-5 py-3 whitespace-nowrap text-sm">
                                            <div className="h-4 bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 rounded animate-pulse" style={{width: `${60 + Math.random() * 80}px`}}></div>
                                        </td>
                                    ))}
                                    {canEditRows && (
                                        <td className="px-5 py-3 text-center sticky right-0 bg-slate-50/95">
                                            <div className="flex justify-center gap-2">
                                                <div className="h-6 w-6 bg-slate-200 rounded-full animate-pulse"></div>
                                                <div className="h-6 w-6 bg-slate-200 rounded-full animate-pulse"></div>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))
                        ) : filteredAndSortedData.length === 0 ? (
                                                        <tr><td colSpan={currentConfig.columns.length + 1 + (canEditRows ? 1 : 0)} className="px-6 py-12 text-center text-slate-400 font-medium">No records found.</td></tr>
                        ) : (
                            filteredAndSortedData.map((row, rIdx) => (
                                <tr key={rIdx} className="hover:bg-slate-50/80 transition-colors">
                                    <td className="px-5 py-3 whitespace-nowrap text-sm font-bold text-slate-500">{rIdx + 1}</td>
                                    {currentConfig.columns.map((col, cIdx) => {
                                        // Avatar columns
                                        if (col.isAvatar) {
                                          const picPath = col.picPath || '';
                                          const picSrc = getMemberPic(row, picPath);
                                          const memberName = getNestedValue(row, col.key);
                                          return (
                                            <td key={cIdx} className="px-4 py-4 whitespace-nowrap">
                                              <div className="flex items-center gap-3">
                                                <Avatar src={picSrc} name={memberName} />
                                                <span className="font-medium text-slate-700 truncate max-w-[200px]">{memberName}</span>
                                              </div>
                                            </td>
                                          );
                                        }
                                        let val = getNestedValue(row, col.key);
                                        // Formatting
                                        if (col.isDate && val) val = format(new Date(val), 'dd MMM, yyyy');
                                        if (col.isCurrency && val) val = `₹ ${Number(val).toLocaleString('en-IN')}`;
                                        
                                        return <td key={cIdx} className="px-5 py-3 whitespace-nowrap text-sm font-medium text-slate-700">{val || '-'}</td>;
                                    })}
                                                                        {canEditRows && (
                                                                            <td className="px-5 py-3 whitespace-nowrap text-sm sticky right-0 bg-white z-10 border-l border-slate-100">
                                                                                <div className="flex items-center gap-2">
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => openEditModal(row)}
                                                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                                                                                        title="Edit"
                                                                                    >
                                                                                        <Pencil className="w-3.5 h-3.5" />
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => handleDelete(row)}
                                                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                                                                                        title="Delete"
                                                                                    >
                                                                                        <Trash2 className="w-3.5 h-3.5" />
                                                                                    </button>
                                                                                </div>
                                                                            </td>
                                                                        )}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>

                {showEditModal && canEditRows && (
                    <div className="fixed inset-0 bg-slate-900/50 flex justify-center items-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col" style={{maxHeight: '90vh'}}>
                            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center flex-shrink-0">
                                <div>
                                    <h3 className="text-lg font-bold text-slate-800">
                                        Edit {resolvedType === 'revenue' ? 'Business Revenue' : resolvedType === 'references' ? 'Reference Pass' : 'Personal Meeting'}
                                    </h3>
                                    <p className="text-xs text-slate-400 mt-0.5">Update the record details below</p>
                                </div>
                                <button onClick={closeEditModal} className="text-slate-400 hover:text-slate-600 p-1.5 rounded-full hover:bg-slate-100 transition-colors">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="overflow-y-auto flex-1 px-6 py-5">
                            <form id="editForm" onSubmit={handleEditSubmit} className="space-y-5">
                                <MemberSearchField
                                    label={resolvedType === 'meetings' ? 'Host Member *' : 'From Member *'}
                                    value={editForm.from_member_name}
                                    memberId={editForm.from_member_id}
                                    onChange={(e) => {
                                        setEditForm(prev => ({ ...prev, from_member_name: e.target.value, from_member_id: '' }));
                                        searchMembers(e.target.value, setFromMemberResults, setIsSearchingFrom);
                                    }}
                                    onSelect={(m) => {
                                        setEditForm(prev => ({ ...prev, from_member_id: m.user_id, from_member_name: m.full_name }));
                                        setFromMemberResults([]);
                                    }}
                                    onClear={() => {
                                        setEditForm(prev => ({ ...prev, from_member_id: '', from_member_name: '' }));
                                        setFromMemberResults([]);
                                    }}
                                    results={fromMemberResults}
                                    isSearching={isSearchingFrom}
                                    required
                                />

                                <MemberSearchField
                                    label={resolvedType === 'meetings' ? 'Visitor Member *' : 'To Member *'}
                                    value={editForm.to_member_name}
                                    memberId={editForm.to_member_id}
                                    onChange={(e) => {
                                        setEditForm(prev => ({ ...prev, to_member_name: e.target.value, to_member_id: '' }));
                                        searchMembers(e.target.value, setToMemberResults, setIsSearchingTo);
                                    }}
                                    onSelect={(m) => {
                                        setEditForm(prev => ({ ...prev, to_member_id: m.user_id, to_member_name: m.full_name }));
                                        setToMemberResults([]);
                                    }}
                                    onClear={() => {
                                        setEditForm(prev => ({ ...prev, to_member_id: '', to_member_name: '' }));
                                        setToMemberResults([]);
                                    }}
                                    results={toMemberResults}
                                    isSearching={isSearchingTo}
                                    required
                                />

                                {resolvedType === 'references' && (
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Ref Name</label>
                                            <input type="text"
                                                value={editForm.refName || ''}
                                                onChange={(e) => setEditForm(prev => ({ ...prev, refName: e.target.value }))}
                                                placeholder="Reference name"
                                                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Phone</label>
                                            <input type="tel"
                                                value={editForm.phone || ''}
                                                onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                                                placeholder="Phone number"
                                                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                    </div>
                                )}

                                {resolvedType === 'revenue' && (
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Amount</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={editForm.amount}
                                            onChange={(e) => setEditForm(prev => ({ ...prev, amount: e.target.value }))}
                                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            required
                                        />
                                    </div>
                                )}

                                {resolvedType === 'meetings' ? (
                                    <>
                                        <div>
                                            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Location</label>
                                            <select
                                                value={editForm.location}
                                                onChange={(e) => setEditForm(prev => ({ ...prev, location: e.target.value }))}
                                                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                required
                                            >
                                                <option value="my place">My Place</option>
                                                <option value="your place">Your Place</option>
                                                <option value="other">Other</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Meeting Date</label>
                                            <input
                                                type="date"
                                                value={editForm.meeting_date}
                                                onChange={(e) => setEditForm(prev => ({ ...prev, meeting_date: e.target.value }))}
                                                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Date</label>
                                        <input
                                            type="date"
                                            value={editForm.date}
                                            onChange={(e) => setEditForm(prev => ({ ...prev, date: e.target.value }))}
                                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            required
                                        />
                                    </div>
                                )}

                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Remarks</label>
                                    <textarea
                                        rows={3}
                                        value={editForm.remarks}
                                        onChange={(e) => setEditForm(prev => ({ ...prev, remarks: e.target.value }))}
                                        className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter remarks"
                                    />
                                </div>

                                <div className="pt-1 pb-1 flex justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={closeEditModal}
                                        className="px-5 py-2.5 rounded-xl text-sm font-bold border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        form="editForm"
                                        type="submit"
                                        disabled={editSubmitting}
                                        className="px-5 py-2.5 rounded-xl text-sm font-bold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 transition-colors"
                                    >
                                        {editSubmitting ? 'Saving...' : 'Update Record'}
                                    </button>
                                </div>
                            </form>
                            </div>
                        </div>
                    </div>
                )}

                {showAddModal && canEditRows && (
                    <div className="fixed inset-0 bg-slate-900/50 flex justify-center items-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col" style={{maxHeight: '90vh'}}>
                            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center flex-shrink-0">
                                <div>
                                    <h3 className="text-lg font-bold text-slate-800">
                                        Add {resolvedType === 'revenue' ? 'Business Revenue' : resolvedType === 'references' ? 'Reference Pass' : 'Personal Meeting'}
                                    </h3>
                                    <p className="text-xs text-slate-400 mt-0.5">Fill in the details to create a new record</p>
                                </div>
                                <button onClick={closeAddModal} className="text-slate-400 hover:text-slate-600 p-1.5 rounded-full hover:bg-slate-100 transition-colors">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="overflow-y-auto flex-1 px-6 py-5">
                            <form id="addForm" onSubmit={handleAddSubmit} className="space-y-5">
                                <MemberSearchField
                                    label={resolvedType === 'meetings' ? 'Host Member *' : 'From Member *'}
                                    value={resolvedType === 'meetings' ? addForm.host_name : addForm.from_member_name}
                                    memberId={resolvedType === 'meetings' ? addForm.host_id : addForm.from_member_id}
                                    onChange={(e) => {
                                        if (resolvedType === 'meetings') {
                                            setAddForm(prev => ({ ...prev, host_name: e.target.value, host_id: '' }));
                                        } else {
                                            setAddForm(prev => ({ ...prev, from_member_name: e.target.value, from_member_id: '' }));
                                        }
                                        searchMembers(e.target.value, setAddFromResults, setIsAddSearchingFrom);
                                    }}
                                    onSelect={(m) => {
                                        if (resolvedType === 'meetings') {
                                            setAddForm(prev => ({ ...prev, host_id: m.user_id, host_name: m.full_name }));
                                        } else {
                                            setAddForm(prev => ({ ...prev, from_member_id: m.user_id, from_member_name: m.full_name }));
                                        }
                                        setAddFromResults([]);
                                    }}
                                    onClear={() => {
                                        if (resolvedType === 'meetings') {
                                            setAddForm(prev => ({ ...prev, host_id: '', host_name: '' }));
                                        } else {
                                            setAddForm(prev => ({ ...prev, from_member_id: '', from_member_name: '' }));
                                        }
                                        setAddFromResults([]);
                                    }}
                                    results={addFromResults}
                                    isSearching={isAddSearchingFrom}
                                    required
                                />

                                <MemberSearchField
                                    label={resolvedType === 'meetings' ? 'Visitor Member *' : 'To Member *'}
                                    value={resolvedType === 'meetings' ? addForm.visitor_name : addForm.to_member_name}
                                    memberId={resolvedType === 'meetings' ? addForm.visitor_id : addForm.to_member_id}
                                    onChange={(e) => {
                                        if (resolvedType === 'meetings') {
                                            setAddForm(prev => ({ ...prev, visitor_name: e.target.value, visitor_id: '' }));
                                        } else {
                                            setAddForm(prev => ({ ...prev, to_member_name: e.target.value, to_member_id: '' }));
                                        }
                                        searchMembers(e.target.value, setAddToResults, setIsAddSearchingTo);
                                    }}
                                    onSelect={(m) => {
                                        if (resolvedType === 'meetings') {
                                            setAddForm(prev => ({ ...prev, visitor_id: m.user_id, visitor_name: m.full_name }));
                                        } else {
                                            setAddForm(prev => ({ ...prev, to_member_id: m.user_id, to_member_name: m.full_name }));
                                        }
                                        setAddToResults([]);
                                    }}
                                    onClear={() => {
                                        if (resolvedType === 'meetings') {
                                            setAddForm(prev => ({ ...prev, visitor_id: '', visitor_name: '' }));
                                        } else {
                                            setAddForm(prev => ({ ...prev, to_member_id: '', to_member_name: '' }));
                                        }
                                        setAddToResults([]);
                                    }}
                                    results={addToResults}
                                    isSearching={isAddSearchingTo}
                                    required
                                />

                                {/* Revenue: Amount */}
                                {resolvedType === 'revenue' && (
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Amount *</label>
                                        <input type="number" min="0"
                                            value={addForm.amount}
                                            onChange={(e) => setAddForm(prev => ({ ...prev, amount: e.target.value }))}
                                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            required
                                        />
                                    </div>
                                )}

                                {/* References: Ref Name + Phone */}
                                {resolvedType === 'references' && (
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Ref Name</label>
                                            <input type="text"
                                                value={addForm.refName}
                                                onChange={(e) => setAddForm(prev => ({ ...prev, refName: e.target.value }))}
                                                placeholder="Reference name"
                                                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Phone</label>
                                            <input type="tel"
                                                value={addForm.phone}
                                                onChange={(e) => setAddForm(prev => ({ ...prev, phone: e.target.value }))}
                                                placeholder="Phone number"
                                                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Meetings: Location + Date + Time + Image */}
                                {resolvedType === 'meetings' ? (
                                    <>
                                        <div>
                                            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Location *</label>
                                            <select
                                                value={addForm.location}
                                                onChange={(e) => setAddForm(prev => ({ ...prev, location: e.target.value }))}
                                                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                required
                                            >
                                                <option value="my place">My Place</option>
                                                <option value="your place">Your Place</option>
                                                <option value="other">Other</option>
                                            </select>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Meeting Date</label>
                                                <input type="date"
                                                    value={addForm.meeting_date}
                                                    onChange={(e) => setAddForm(prev => ({ ...prev, meeting_date: e.target.value }))}
                                                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                />
                                            </div>

                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Meeting Image</label>
                                            <input type="file" accept="image/*"
                                                onChange={(e) => setAddImageFile(e.target.files[0] || null)}
                                                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Date</label>
                                        <input type="date"
                                            value={addForm.date}
                                            onChange={(e) => setAddForm(prev => ({ ...prev, date: e.target.value }))}
                                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                )}

                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Remarks</label>
                                    <textarea rows={3}
                                        value={addForm.remarks}
                                        onChange={(e) => setAddForm(prev => ({ ...prev, remarks: e.target.value }))}
                                        className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Enter remarks"
                                    />
                                </div>

                                <div className="pt-1 pb-1 flex justify-end gap-3">
                                    <button type="button" onClick={closeAddModal}
                                        className="px-5 py-2.5 rounded-xl text-sm font-bold border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors">
                                        Cancel
                                    </button>
                                    <button form="addForm" type="submit" disabled={addSubmitting}
                                        className="px-5 py-2.5 rounded-xl text-sm font-bold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 transition-colors">
                                        {addSubmitting ? 'Saving...' : 'Add Record'}
                                    </button>
                                </div>
                            </form>
                            </div>
                        </div>
                    </div>
                )}
      </div>
    </div>
  );
};

export default ViewData;